import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/theme.css';
import './styles/livery.css';
import App from './App.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import { diag, errorSummary, installGlobalHandlers, consoleMirror } from './diagnostics/log';
import { setDiceRandomSource, setInitiativeRandomSource } from './engine/dice';
import { ToastProvider } from './contexts/ToastContext.jsx';
import { backend, initBackend } from './backend/app';
import { __e2eSetMuted, __e2eFireReconnect, __e2eRedeliver } from './multiplayer';
import { readFuzzTap } from './diagnostics/fuzzTap';
import { enableWriteTrace, readWriteTrace } from './diagnostics/writeTrace';

// E2E deterministic dice support - two-phase arm/activate pattern.
// setDiceSequence arms the sequence (stores values) but does NOT override Math.random yet.
// activateDice overrides Math.random with the armed sequence. This prevents the probability
// engine's async Monte Carlo from consuming seeded dice between arm and attack.
if (import.meta.env.VITE_USE_EMULATOR === 'true') {
  // copyText leaves the last copied text on the window for a spec (v8.362.0).
  window.__e2eCaptureCopies = true;
  // The sync chaos seams (v8.362.3): drop every delivery, fire the reconnect listeners.
  window.__e2eMuteSync = (on) => __e2eSetMuted(!!on);
  window.__e2eFireReconnect = () => __e2eFireReconnect();
  // ...and hand each listener the state it missed, as a real reconnect does (the fuzzer's unmute).
  window.__e2eRedeliver = () => __e2eRedeliver();
  // The fuzzer's peepholes (dev-docs/plans/completed/fuzz.md): the diagnostic log, and
  // this client's table as data (the shell registers it - hooks/useFuzzTap).
  window.__fuzzDiag = () => diag.entries();
  window.__fuzzState = () => readFuzzTap();
  // ...and whether its realtime stream is up: a screen still reconnecting is behind, not drifting.
  window.__fuzzStreamUp = () => backend.streamConnected?.() ?? true;
  // ...and who changed the roster, from where (a stack per write - sandbox only).
  enableWriteTrace(true);
  window.__fuzzWrites = () => readWriteTrace();
  // ...and its oracles, run IN the page: they import the engine, which the
  // browser fuzzer's Node side cannot load. A dynamic import in this block,
  // so a production build drops it with the block.
  import('./engine/fuzz/clients').then((oracles) => {
    window.__fuzzOracles = oracles;
  });
  const originalRandom = Math.random;
  let diceSequence = null;
  let diceIndex = 0;

  // Seeds the DICE, not global Math.random.
  //
  // It used to replace `Math.random` outright, which meant every
  // unrelated caller in the app - an id being minted, a retry jitter -
  // stole a value and shifted every later roll. Worse, the sequence
  // CYCLES, so a theft never failed: it handed back a plausible wrong
  // number from earlier in the sequence. Two thefts were found and fixed
  // in a day and a third was still at large.
  //
  // `engine/dice.ts` is the one funnel every real roll goes through, so
  // seeding it makes the whole class of bug impossible.
  const seededSource = () => {
    if (!diceSequence || diceSequence.length === 0) return originalRandom();
    // Cycling is deliberate and relied upon: `setFixedRoll` seeds a single
    // value and expects every subsequent roll to take it.
    const val = diceSequence[diceIndex % diceSequence.length];
    diceIndex++;
    return val;
  };

  window.__e2eSetDiceSequence = (sequence) => {
    diceSequence = sequence;
    diceIndex = 0;
    setDiceRandomSource(seededSource);
  };

  // Kept for the specs that call it. It no longer has a job to do - the
  // probability engines draw from Math.random and can no longer reach the
  // dice sequence at all - but resetting the index is still harmless and
  // removing the export would break callers for no gain.
  window.__e2eActivateDice = () => {
    diceIndex = 0;
    setDiceRandomSource(seededSource);
  };

  window.__e2eResetDice = () => {
    diceSequence = null;
    diceIndex = 0;
    setDiceRandomSource(null);
  };

  // How many dice have been drawn. Exposed so a failing spec can MEASURE
  // what it consumed instead of anyone guessing at it - which is how the
  // two thefts above were eventually found, the hard way.
  window.__e2eDiceIndex = () => diceIndex;

  // ── The initiative channel (v8.66.0) ──────────────────────
  //
  // Initiative is rolled on admission, so a spec that loads four
  // characters draws four d10s before it attacks. On the shared
  // sequence that shifts every later roll - and because the sequence
  // CYCLES it would not fail, it would hand back a plausible wrong
  // number, which is the failure mode the comment above was written
  // about. So initiative gets its own sequence and its own index.
  //
  // A spec that does not seed this one gets real randomness for
  // initiative and its combat sequence exactly as before.
  let initSequence = null;
  let initIndex = 0;

  const seededInitiative = () => {
    if (!initSequence || initSequence.length === 0) return originalRandom();
    const val = initSequence[initIndex % initSequence.length]; // cycles, like the dice
    initIndex++;
    return val;
  };

  /**
   * PIN THE INITIATIVE CHANNEL, ALWAYS.
   *
   * `rollInitiativeDie()` falls through to the ordinary dice draw when
   * no initiative source is set - which is right in production, where
   * there is one channel and it is `Math.random`. Under the harness it
   * is exactly wrong: `draw()` IS the seeded combat sequence, so a spec
   * that seeds dice but not initiative has every admission roll steal
   * from it. Gotcha #5's failure mode, reintroduced by the very seam
   * meant to prevent it - and silent, because the sequence cycles.
   *
   * So the harness never leaves the initiative source null. Seeded when
   * a spec asks for it, real randomness otherwise, but never the combat
   * sequence.
   */
  const pinInitiative = () => setInitiativeRandomSource(initSequence ? seededInitiative : originalRandom);

  window.__e2eSetInitiativeSequence = (sequence) => {
    initSequence = sequence;
    initIndex = 0;
    pinInitiative();
  };

  window.__e2eResetInitiative = () => {
    initSequence = null;
    initIndex = 0;
    pinInitiative();
  };

  window.__e2eInitiativeIndex = () => initIndex;

  // Engaged from the start, not on first use: a spec that never touches
  // initiative must still not have its dice stolen by an admission roll.
  pinInitiative();
}

/**
 * CONFIG FIRST, THEN MOUNT.
 *
 * `initBackend` reads /config.json and dynamic-imports the one adapter it
 * names, so the app must not render until it resolves - every consumer of
 * `backend` would otherwise throw. It is one same-origin fetch of a tiny file,
 * and it falls back to Firebase when there is no file at all, which is what a
 * Firebase Hosting deployment and `npm run dev` both do.
 *
 * A FAILURE HERE IS FATAL AND MUST LOOK LIKE IT. There is no application
 * without a backend, and a blank page with a console error is the worst way to
 * say so - ErrorBoundary is not mounted yet, so this renders the message
 * itself.
 */
// The emulator has anonymous sign-in on, and the specs drive the player's
// route in through it. Production Firebase does NOT - it is a console toggle,
// and the adapter deliberately will not claim a provider it cannot verify - so
// this says so here rather than in the shipped public/config.json.
// WHICH BACKEND THE SPECS DRIVE. Default Firebase, so an unset variable
// behaves exactly as before; `VITE_E2E_BACKEND=pocketbase` points the same
// specs at a PocketBase, which is the whole reason the sign-in helpers below
// go through the port instead of through Firebase's SDK.
const E2E_CONFIG =
  import.meta.env.VITE_USE_EMULATOR === 'true'
    ? import.meta.env.VITE_E2E_BACKEND === 'pocketbase'
      ? {
          backend: 'pocketbase',
          pocketbase: { url: import.meta.env.VITE_E2E_PB_URL || 'http://127.0.0.1:8090' },
        }
      : // The emulator has anonymous and password sign-in on; production
        // Firebase has neither, which is why this is stated here and not in
        // the shipped public/config.json.
        { backend: 'firebase', firebase: { methods: ['anonymous', 'google', 'password'] } }
    : undefined;

// Before anything can throw: an error outside a render and a promise
// nobody awaited both land in the bug report instead of nowhere.
installGlobalHandlers(window);
// THE SDKs SPEAK ONLY TO THE CONSOLE. Firestore reports a write stream
// that keeps failing - an oversized document, a payload the server refuses
// at the transport - as `console.error`, and nowhere the app can see. A
// stuck write hung every later write behind it and the report said
// nothing (v8.310.18). Mirror the console into the buffer, once, briefly.
/** One console argument as text: an Error by name and message, a string as is, the rest as JSON. */
const consoleArgText = (a) =>
  a instanceof Error ? `${a.name}: ${a.message}` : typeof a === 'string' ? a : JSON.stringify(a);

/** A console method that also records its line (bar the app's own "[tag]" lines) in the bug report. */
function mirrored(original, level) {
  return (...args) => {
    original(...args);
    try {
      const text = args.map(consoleArgText).join(' ').slice(0, 400);
      if (!text.startsWith('[')) diag.record(level, 'console', text);
    } catch {
      /* a value that will not stringify is not worth a second error */
    }
  };
}
console.error = mirrored(console.error.bind(console), 'error');
console.warn = mirrored(console.warn.bind(console), 'warn');
if (import.meta.env.DEV) diag.mirrorTo(consoleMirror);

initBackend(E2E_CONFIG)
  .then((backend) => {
    diag.setContext({ version: __APP_VERSION__, buildTime: __BUILD_TIME__, backend: backend.name });
    if (import.meta.env.VITE_USE_EMULATOR === 'true') {
      // THROUGH THE PORT, not through a backend's SDK. These lived in
      // firebase.js and were Firebase-shaped, which meant a spec could only
      // ever drive one backend. Emulator builds only - never production.
      window.__e2eSignIn = (identity, password) => backend.auth.signIn('password', { identity, password });
      window.__e2eAuthReady = () => !!backend.auth.current();
    }
    ReactDOM.createRoot(document.getElementById('root')).render(
      <React.StrictMode>
        <ErrorBoundary>
          <ToastProvider>
            <App />
          </ToastProvider>
        </ErrorBoundary>
      </React.StrictMode>,
    );
  })
  .catch((err) => {
    console.error('[backend] failed to start', err);
    diag.error('backend', 'failed to start', errorSummary(err));
    const root = document.getElementById('root');
    if (root) {
      root.textContent = `Could not reach the backend: ${err?.message ?? err}. Check /config.json and reload.`;
      root.style.cssText = 'padding:2rem;font-family:system-ui;color:#e5e7eb;background:#111827;min-height:100vh';
    }
  });
